import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { memoize, nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";

import { LicenseCollection } from "@courses-models/license";

import { api } from "@courses-models/user/src/ts/config";
import {
  UserModel,
  UserTypes,
} from "@courses-models/user/src/ts/models/UserModel";

/**
 * Describes data returned by User/GetEkeyUserDetailsByEmail from Gyldendal
 * Access Service.
 */
type User_GetEkeyUserDetailsByEmail_Response = {
  UserId: string;
  UserName: string;
  FirstName: string;
  LastName: string;
  Email: string | null;
};

/**
 * Specification of data required to instantiate a new UnicUserModel.
 */
type EkeyUserData = {
  ekeyId: string;
  id: string;
  mail: string | null;
  name: string;
};

/**
 * Memory cache of users that have already been fetched by the client, so that
 * we don't need to fetch the same users over and over again.
 */
const cache = new EntityStore<EkeyUserModel>({ ttl: 600 });

/**
 * Specification of users from the Ekey identity provider, including all api
 * integrations required to load data.
 */
export class EkeyUserModel extends UserModel {
  /**
   * Specifies the globally unique id of the user within the e-key system.
   */
  public readonly ekeyId: string;

  /**
   * Apply data initially when creating a user model.
   *
   * You shouldn't create instances using new EkeyUserModel(...) directly, use
   * EkeyUserModel.CREATE instead. That way we can ensure cache integrity.
   */
  constructor(data: EkeyUserData) {
    super(data);
    this.ekeyId = data.ekeyId;

    // Cache the user model instance for future reference, so we don't need
    // to re-fetch it again later on
    cache.set(this.id, this);
  }

  /**
   * Helper that'll return a unic user model based on the given id.
   */
  @nonConcurrent()
  public static async GET_BY_ID(id: string): Promise<EkeyUserModel> {
    // If this user has already been cached, then return an instance of this
    // method directly from there
    if (cache.has(id)) {
      return <EkeyUserModel>cache.get(id);
    }

    // Fetch data about the user from Gyldendal Access Service
    const endpoint = `${api.gyldendalAccessService(
      undefined
    )}/api/User/GetEkeyUserDetailsByEmail?userEmail=${encodeURIComponent(id)}`;
    const response = await axios.get<User_GetEkeyUserDetailsByEmail_Response>(
      gGateway(endpoint)
    );

    return new EkeyUserModel({
      id,
      ekeyId: response.data.UserId,
      mail: response.data.Email,
      name: `${response.data.FirstName} ${response.data.LastName}`,
    });
  }

  /**
   * Helper that'll create a new EkeyUserModel based on the given data.
   */
  public static CREATE(data: EkeyUserData): EkeyUserModel {
    // If the model already exists in our cache, then return it directly
    // from there
    if (cache.has(data.id)) {
      return <EkeyUserModel>cache.get(data.id);
    }

    // ... Otherwise create a new model
    return new EkeyUserModel(data);
  }

  /**
   * E-key users cannot be associated with a class, so always return null.
   */
  public async getClass(): Promise<null> {
    // tslint:disable-next-line: no-null-keyword
    return null;
  }

  /**
   * Helper that'll resolve the institution id associated with the user, when
   * he's accessing the given product.
   */
  @memoize()
  public async getInstitutionId(productId: string): Promise<string | null> {
    // Generate both isbn10 and isbn13 based on the given product id
    const isbn10 = productId.substring(productId.length - 10);
    const isbn13 = `978${isbn10}`;

    // Fetch list of licensed products, so that we can extract the user type
    // associated with the given product
    const licenses = await LicenseCollection.GET_BY_EKEY_USER_ID(this.ekeyId);

    // Loop through and determine if one of the licensed products match the
    // id of the requested one
    for (const license of licenses) {
      // Ignore products that doesn't match the given id
      if (
        license.id !== productId &&
        license.productIsbn !== isbn10 &&
        license.productIsbn !== isbn13
      ) {
        continue;
      }

      // ... Otherwise return the institution id from the license, if one
      // was given
      if (license.institutionId) {
        return license.institutionId;
      }
    }

    // If we get here, no institution id could be identified - fall back to
    // null
    // tslint:disable-next-line: no-null-keyword
    return null;
  }

  /**
   * Helper that'll look up the user's type based on his license for the given
   * product.
   */
  @memoize()
  public async getUserType(productId: string): Promise<UserTypes | null> {
    // Generate both isbn10 and isbn13 based on the given product id
    const isbn10 = productId.substring(productId.length - 10);
    const isbn13 = `978${isbn10}`;

    // Fetch list of licensed products, so that we can extract the user type
    // associated with the given product
    const licenses = await LicenseCollection.GET_BY_EKEY_USER_ID(this.ekeyId);

    // Loop through and determine if one of the licensed produces match the
    // id of the requested one
    for (const license of licenses) {
      // Ignore products that doesn't match the given id
      if (
        license.id !== productId &&
        license.productIsbn !== isbn10 &&
        license.productIsbn !== isbn13
      ) {
        continue;
      }

      // ... Otherwise return the type from this licensed product, if one
      // was identified
      const userType = getUserTypeFromLicenseRoles(license.roleTypes);

      if (userType !== null) {
        return userType;
      }
    }

    // If we get here, no user type could be identified - fall back to null
    // tslint:disable-next-line: no-null-keyword
    return null;
  }

  /**
   * Allow easy debugging by providing a toString helper.
   */
  public toString(): string {
    return `[UnicUserModel ${this.id}]`;
  }
}

/**
 * Helper that iterates over the list of roles associated with a license, and
 * converts it into our well-defined UserTypes enumeration.
 */
function getUserTypeFromLicenseRoles(roles: number[]): UserTypes | null {
  // tslint:disable-next-line: no-null-keyword
  let userType: UserTypes | null = null;

  for (const role of roles) {
    switch (role) {
      case 1:
      case 2:
        userType = UserTypes.TEACHER;

      case 3:
        userType = UserTypes.STUDENT;

      default:
      // Ignore unknown roles
    }
  }

  return userType;
}
